<script setup>
import { ref, computed } from 'vue';
import {useForm, usePage} from '@inertiajs/vue3';
import {trans} from "@/Utils/translation.utils.js";
import ReCaptcha from "@/Components/Partials/ReCaptcha.vue";

const props = defineProps({
  reviews: {
    type: [Array, Object],
    default: () => []
  },
  entityType: {
    type: String,
    default: 'product'
  },
  entityId: {
    type: [Number, String],
    default: null
  },
  rating: {
    type: Number,
    default: 0
  },
});

const form = useForm({
  entity_type: props.entityType,
  entity_id: props.entityId,
  name: '',
  email: '',
  rating: 5,
  comment: '',
  recaptchaToken: null
});

const formErrors = ref({});

const handleToken = (token) => {
    formErrors.value.recaptchaToken = '';
    form.recaptchaToken = token;
};
const handleExpired = () => {
    formErrors.value.recaptchaToken = trans('front_payment.validation.recaptcha_expired');
};

const currentLocale = usePage().props.currentLocal;

const formVisible = ref(false);
const showAllReviews = ref(false);
const successMessage = ref('');

const filteredReviews = computed(() => {
  return showAllReviews.value ? props.reviews : props.reviews.slice(0, 8);
});

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const toggleForm = () => {
  formVisible.value = !formVisible.value;
  form.reset();
  form.clearErrors();
  successMessage.value = '';
};

const setRating = (rating) => {
  form.rating = rating;
};

const submitReview = () => {
  form.post(route('reviews.store'), {
    preserveScroll: true,
    onSuccess: () => {
      successMessage.value = trans('reviews.success_message');
      form.reset();
      form.entity_type = props.entityType;
      form.entity_id = props.entityId;

      setTimeout(() => {
        formVisible.value = false;
        successMessage.value = '';
      }, 3000);
    },
    onError: (errors) => {
      console.error(trans('reviews.error_message'), errors);
    }
  });
}
</script>

<template>
  <div class="reviews-cover" id="reviews-cover" v-if="reviews.length > 0">
    <div class="row">
      <div class="col-md-12">
        <h2 class="text-center mb-4">{{ trans('reviews.clients_say_title') }}</h2>
      </div>
    </div>
    <div class="row mb-4">
       <div class="col-md-12 col-lg-9 d-flex align-items-center flex-wrap">
        <div class="reviews_top">{{ trans('reviews.clients_rate_us') }}</div>
        <div style="width: 10px"></div>
        <div class="reviews_top_reiting-info">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.6677 21.8034L11.9996 18.593L17.3315 21.8034C17.5671 21.9532 17.8133 22.0174 18.0703 21.996C18.3272 21.9746 18.5521 21.889 18.7448 21.7392C18.9375 21.5894 19.0874 21.4022 19.1945 21.178C19.3015 20.9528 19.3229 20.7012 19.2587 20.4228L17.8454 14.3552L22.567 10.278C22.7812 10.0854 22.9152 9.86582 22.9692 9.61928C23.0224 9.37356 23.006 9.133 22.9204 8.89758C22.8348 8.66214 22.7062 8.46952 22.535 8.3197C22.3636 8.16988 22.128 8.07358 21.8284 8.03076L15.597 7.485L13.188 1.7705C13.081 1.51366 12.9152 1.32104 12.6908 1.19262C12.4656 1.06421 12.2351 1 11.9996 1C11.7641 1 11.5341 1.06421 11.3097 1.19262C11.0844 1.32104 10.9182 1.51366 10.8112 1.7705L8.40216 7.485L2.1709 8.03076C1.87111 8.07358 1.63557 8.16988 1.46426 8.3197C1.29295 8.46952 1.16448 8.66214 1.07882 8.89758C0.993169 9.133 0.977323 9.37356 1.03128 9.61928C1.08439 9.86582 1.21801 10.0854 1.43214 10.278L6.15378 14.3552L4.7405 20.4228C4.67626 20.7012 4.69766 20.9528 4.80474 21.178C4.9118 21.4022 5.0617 21.5894 5.25442 21.7392C5.44714 21.889 5.67198 21.9746 5.92894 21.996C6.1859 22.0174 6.43214 21.9532 6.6677 21.8034Z" fill="#FF6F00"/>
          </svg>
          <span>{{ rating }}/5</span>
          — {{ trans('reviews.based_on', {count: reviews.length}) }}
        </div>
      </div>
      <div class="col-md-12 col-lg-3">
        <div class="btn btn-green btn-success w-100 mt-lg-0 mt-3" @click="toggleForm">{{ trans('reviews.leave_review') }}</div>
      </div>
    </div>
    <div class="reviews-form" :class="{'active': formVisible}">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <form @submit.prevent="submitReview" class="reviews-form-item">
            <div class="form-title">{{ trans('reviews.leave_review') }}</div>
            <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>
            <div v-if="form.errors.general" class="alert alert-danger">{{ form.errors.general }}</div>

            <div class="form-group">
              <label for="reviewName" class="form-label">{{ trans('reviews.your_name') }}</label>
              <input
                  type="text"
                  class="form-control form-control-lg"
                  id="reviewName"
                  v-model="form.name"
                  :placeholder="trans('reviews.your_name')"
                  :class="{'is-invalid': form.errors.name}"
              >
              <div v-if="form.errors.name" class="invalid-feedback">{{ form.errors.name }}</div>
            </div>

            <div class="form-group">
              <label class="form-label" for="reviewEmail">{{ trans('reviews.your_email') }}</label>
              <input
                  type="email"
                  class="form-control form-control-lg"
                  id="reviewEmail"
                  v-model="form.email"
                  :placeholder="trans('reviews.your_email')"
                  :class="{'is-invalid': form.errors.email}"
              >
              <div v-if="form.errors.email" class="invalid-feedback">{{ form.errors.email }}</div>
            </div>

            <div class="form-group d-flex flex-column">
              <label for="reviewRating" class="form-label">{{ trans('reviews.rating') }}</label>
              <div class="rate-info">
                <div class="rate d-flex">
                    <span
                        v-for="star in 5"
                        :key="star"
                        @click="setRating(star)"
                        :class="{'active': form.rating >= star}"
                        class="star-rating"
                    >
                        <img :src="form.rating >= star ? '/../img/icons/icon-review-star.svg' : '/../img/icons/icon-review-star3.svg'" alt="" width="24" height="24">
                    </span>
                </div>
                <span class="rating-note">{{ form.rating }}/5</span>
              </div>
              <div v-if="form.errors.rating" class="text-danger">{{ form.errors.rating }}</div>
            </div>

            <div class="form-group">
              <label for="reviewComment" class="form-label">{{ trans('reviews.your_review') }}</label>
              <textarea
                  class="form-control form-control-lg"
                  id="reviewComment"
                  rows="3"
                  v-model="form.comment"
                  :placeholder="trans('reviews.your_review')"
                  :class="{'is-invalid': form.errors.comment}"
              ></textarea>
              <div v-if="form.errors.comment" class="invalid-feedback">{{ form.errors.comment }}</div>
            </div>

            <div v-if="form.errors.recaptchaToken" class="text-danger mb-3">{{ form.errors.recaptchaToken }}</div>
            <div class="row g-0 has-validation mb-3">
              <div class="col-12 d-flex justify-content-center">
                  <ReCaptcha @tokenVerified="handleToken" @tokenExpired="handleExpired" :error="formErrors.recaptchaToken"/>
              </div>
            </div>

            <button
                type="submit"
                class="btn btn-green btn-success"
                :disabled="form.processing"
            >
              <span v-if="form.processing">{{ trans('reviews.sending') }}</span>
              <span v-else>{{ trans('reviews.send_review') }}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
    <div class="reviews-list-items" v-if="reviews.length">
      <div class="row">
        <div class="col-lg-3 col-md-6" v-for="review in filteredReviews" :key="review.id">
          <div class="review-item">
            <div class="review-item__top">
              <div class="reviews-item__top--start">
                <img v-for="star in 5" :key="star"
                     :src="review.rating >= star ? '/../img/icons/icon-review-star.svg' : '/../img/icons/icon-review-star3.svg'"
                     alt="star">
              </div>
              <div class="reviews-item__top--date">{{ formatDate(review.created_at) }}</div>
            </div>
            <div class="review-item__body">
              {{ review.comment }}
            </div>
            <div class="review-item__bottom">
              <div class="review-item__bottom--name">{{ review.name }}</div>
              <div class="review-item__bottom--verify">{{ trans('reviews.verified_purchase') }}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.9331 0.870357C7.06685 -0.290346 8.93425 -0.290543 10.0679 0.870357L10.689 1.50805L11.5796 1.49731C13.2022 1.47806 14.5227 2.79852 14.5034 4.42114L14.4927 5.31079L15.1294 5.93286C16.2903 7.0666 16.2904 8.93394 15.1294 10.0676L14.4927 10.6887L14.5034 11.5793C14.5227 13.2019 13.2021 14.5224 11.5796 14.5032L10.689 14.4924L10.0679 15.1291C8.93419 16.2902 7.06684 16.2901 5.9331 15.1291L5.31103 14.4924L4.42138 14.5032C2.79877 14.5225 1.47831 13.2019 1.49755 11.5793L1.5083 10.6887L0.870601 10.0676C-0.290299 8.934 -0.290102 7.0666 0.870601 5.93286L1.5083 5.31079L1.49755 4.42114C1.47823 2.79847 2.79872 1.47798 4.42138 1.49731L5.31103 1.50805L5.9331 0.870357ZM11.7065 5.29321C11.3404 4.92717 10.7614 4.90452 10.3686 5.22485L10.2925 5.29321L6.99951 8.58618L5.70654 7.29321L5.63037 7.22485C5.2376 6.90461 4.65855 6.92713 4.29248 7.29321C3.92673 7.65933 3.90391 8.23846 4.22412 8.6311L4.29248 8.70727L6.29248 10.7073L6.36865 10.7756C6.76129 11.0959 7.3404 11.073 7.70654 10.7073L11.7065 6.70727L11.7749 6.6311C12.0952 6.23833 12.0727 5.65932 11.7065 5.29321Z" fill="#A1AFCC"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center my-5">
      <p>{{ trans('reviews.no_reviews') }}</p>
    </div>
    <div v-if="reviews.length > 8" class="reviews-list__btn" @click="showAllReviews = !showAllReviews">
      {{ showAllReviews ? trans('reviews.hide_reviews') : trans('reviews.show_all_reviews') }}
    </div>
  </div>
</template>
